/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { messages } from '../../common/messages';

const wrapperStyles = css({
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	boxShadow: token('elevation.shadow.overlay'),
});

const labelStyles = css({
	display: 'block',
	color: token('color.text.subtle'),
	marginBottom: token('space.075'),
});

export type ConfigurePanelProps = {
	/** Initial value for the prompt textarea. */
	initialPrompt: string;
	/** Called when the user dismisses the panel. */
	onCancel?: () => void;
	/**
	 * Called when the user clicks **Save**. The persistence and the underlying ProseMirror
	 * transaction are orchestrated by the node view above it.
	 */
	onSave: (prompt: string) => void;
};

/**
 * Inline "Configure block" panel rendered directly underneath an
 * agent-managed bodied extension.
 */
export const ConfigurePanel = ({ initialPrompt, onSave, onCancel }: ConfigurePanelProps): JSX.Element => {
	const intl = useIntl();
	const [value, setValue] = useState<string>(initialPrompt);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);

	// Auto-focus immediately after inserting the block. `preventScroll`
	// avoids the browser scrolling to the portal mount point at the top of the page.
	useEffect(() => {
		textareaRef.current?.focus({ preventScroll: true });
	}, []);

	// Keep local state in sync with external resets (e.g. another collaborator
	// edits the prompt).
	useEffect(() => {
		setValue(initialPrompt);
	}, [initialPrompt]);

	const handleSave = useCallback(() => {
		onSave(value.trim());
	}, [onSave, value]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			setValue(event.currentTarget.value);
		},
		[setValue],
	);

	return (
		<div css={wrapperStyles} data-testid="agent-managed-extension-configure-panel">
			<Stack space="space.150">
				<Heading size="small">{intl.formatMessage(messages.configureBlock)}</Heading>
				<div>
					<label css={labelStyles} htmlFor="agent-managed-extension-prompt">
						{intl.formatMessage(messages.customInstructionsLabel)}
					</label>
					<TextArea
						id="agent-managed-extension-prompt"
						testId="agent-managed-extension-prompt"
						ref={textareaRef}
						minimumRows={3}
						value={value}
						onChange={handleChange}
						placeholder={intl.formatMessage(messages.customInstructionsPlaceholder)}
					/>
				</div>
				<Inline space="space.100" alignBlock="center" alignInline="end">
					{onCancel && (
						<Button appearance="subtle" onClick={onCancel}>
							{intl.formatMessage(messages.cancel)}
						</Button>
					)}
					<Button appearance="primary" onClick={handleSave} testId="agent-managed-extension-save">
						{intl.formatMessage(messages.save)}
					</Button>
				</Inline>
			</Stack>
		</div>
	);
};
